import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { hasPermission, type Permission } from '@/core/auth/permissions.js';
import { useSession } from '@/features/auth';
import { EmptyState, LoadingState } from '@/shared/ui/index.js';

/**
 * Exige sesión. Mientras el estado es "desconocida" espera, en vez de expulsar
 * al usuario antes de que termine la rehidratación del refresh token.
 */
export function RequireAuth({ children }: Readonly<{ readonly children: ReactNode }>) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'desconocida') {
    return <LoadingState label="Verificando sesión..." />;
  }
  if (status === 'anonima') {
    return <Navigate replace state={{ from: location.pathname }} to="/ingresar" />;
  }
  return <>{children}</>;
}

/**
 * Exige un permiso concreto.
 *
 * Es una comodidad de navegación: el API revalida cada permiso. Ocultar la
 * ruta no sustituye la autorización del servidor.
 */
export function RequirePermission({
  permission,
  children,
}: Readonly<{ readonly permission: Permission; readonly children: ReactNode }>) {
  const { user } = useSession();

  if (!hasPermission(user?.permissions, permission)) {
    return (
      <EmptyState
        message="Tu usuario no tiene acceso a esta sección. Consulta con gerencia si crees que es un error."
        title="Sin permiso"
      />
    );
  }
  return <>{children}</>;
}
